<template>
  <section id="filter">
    <div class="tw-relative">
      <form @submit.prevent="submit" @change="isChange = true">
        <div
          class="tw-grid tw-gap-2 tw-rounded tw-bg-white tw-p-4 tw-pb-0 md:tw-grid-cols-4"
        >
          <cl-form-group>
            <cl-form-label label-for="message-type" position="border">
              {{ $t("Date Range") }}
            </cl-form-label>
            <cl-form-select
              id="date-range"
              v-model:value="date_range"
              :options="dateRangeOptions"
            ></cl-form-select>
          </cl-form-group>
          <cl-form-group>
            <cl-form-label label-for="recipient-email" position="border">
              {{ $t("Recipient (To)") }}
            </cl-form-label>
            <cl-form-input
              id="recipient-email"
              name="recipient-email"
              v-model:value="recipient"
              size="medium"
              :disabled="isRecipientStatic"
            />
          </cl-form-group>
          <cl-form-group>
            <cl-form-label label-for="sender-email" position="border">
              {{ $t("Sender (From)") }}
            </cl-form-label>
            <cl-form-input
              id="sender-email"
              name="sender-email"
              v-model:value="sender"
              size="medium"
            />
          </cl-form-group>
          <cl-form-group>
            <cl-form-label label-for="subject" position="border">
              {{ $t("Subject") }}
            </cl-form-label>
            <cl-form-input
              id="subject"
              name="subject"
              v-model:value="subject"
              size="medium"
            />
          </cl-form-group>
        </div>
        <div
          v-if="isAdvancedFilter"
          class="tw-grid tw-gap-2 tw-rounded tw-bg-white tw-p-2 tw-pb-0 md:tw-grid-cols-4"
        >
          <cl-form-group v-if="!isDisabledDate">
            <cl-form-label label-for="date-from" position="border">
              <div>{{ $t("Date From") }}<sup>*</sup></div>
            </cl-form-label>
            <cl-form-input
              type="date"
              id="date-from"
              name="date-from"
              v-model:value="start_date"
              size="medium"
              @on-keydown.prevent
              :max="maxDate"
              :disabled="isDisabledDate"
            />
          </cl-form-group>
          <cl-form-group v-if="!isDisabledDate">
            <cl-form-label label-for="date-to" position="border">
              {{ $t("Date To") }}
            </cl-form-label>
            <cl-form-input
              type="date"
              id="date-to"
              name="date-to"
              v-model:value="end_date"
              size="medium"
              @on-keydown.prevent
              :max="maxDate"
              :min="start_date"
              :disabled="isDisabledDate"
            />
          </cl-form-group>
          <cl-form-group>
            <cl-form-label label-for="envelope-from" position="border">
              {{ $t("Envelope From") }}
            </cl-form-label>
            <cl-form-input
              id="envelope-from"
              name="envelope-from"
              v-model:value="envelope_from"
              size="medium"
            />
          </cl-form-group>
          <cl-form-group>
            <cl-form-label label-for="spamtitan-id" position="border">
              {{ $t("SpamTitan ID") }}
            </cl-form-label>
            <cl-form-input
              id="spamtitan-id"
              name="spamtitan-id"
              v-model:value="id"
              size="medium"
            />
          </cl-form-group>
          <cl-form-group>
            <cl-form-label label-for="ip-address" position="border">
              {{ $t("Source IP address") }}
            </cl-form-label>
            <cl-form-input
              id="ip-address"
              name="ip-address"
              v-model:value="ip"
              size="medium"
            />
          </cl-form-group>
          <cl-form-group>
            <cl-form-label label-for="delivery-status" position="border">
              {{ $t("Delivery Status") }}
            </cl-form-label>
            <cl-form-select
              id="delivery-status"
              v-model:value="delivery_status"
              :options="deliveryStatusOptions"
            ></cl-form-select>
          </cl-form-group>
          <cl-form-group>
            <cl-form-label label-for="message-type" position="border">
              {{ $t("Message Type") }}
            </cl-form-label>
            <c-multiselect
              v-model="type"
              label="text"
              track-by="value"
              openDirection="bottom"
              placeholder="Please select"
              multiple
              taggable
              :searchable="false"
              :showLabels="false"
              :options="messageTypeOptions"
            ></c-multiselect>
          </cl-form-group>
          <div class="md:tw-col-span-2">
            <cl-form-group>
              <cl-form-label label-for="score-key" position="border">
                {{ $t("Score") }}
              </cl-form-label>
              <div class="tw-flex">
                <cl-form-select
                  id="score-key"
                  v-model:value="score_compare"
                  :options="scoreCompareOptions"
                ></cl-form-select>
                <template v-if="score_compare !== '' && score_compare !== '*'">
                  <cl-form-input
                    id="score-first"
                    type="number"
                    name="score-first"
                    v-model:value="score[0]"
                    size="medium"
                    step="0.001"
                  />
                </template>
                <template v-if="score_compare === 'between'">
                  <cl-form-input
                    id="score-second"
                    type="number"
                    name="score-second"
                    v-model:value="score[1]"
                    size="medium"
                    step="0.001"
                  />
                </template>
              </div>
            </cl-form-group>
          </div>
        </div>
        <div class="tw-grid tw-rounded tw-bg-white tw-px-2 sm:tw-grid-cols-1">
          <div class="tw-mb-1.5 tw-flex tw-justify-end">
            <cl-button
              variant="secondary"
              type="submit"
              data-test-id="submit-button"
            >
              <span v-if="!fetching" class="tw-text-white">
                {{ $t("Apply") }}
              </span>
              <span class="px-2" v-else><cl-spinner size="small" /></span>
            </cl-button>
          </div>
        </div>
      </form>
      <button
        variant="secondary"
        @click="toggleAdvancedFilter()"
        class="tw-absolute tw-bottom-4 tw-left-5"
      >
        <div class="tw-align-center tw-flex tw-gap-1">
          <span class="tw-whitespace-nowrap tw-font-bold tw-text-secondary-500">
            {{ $t("Advanced Filter") }}
          </span>
          <div
            class="tw-flex tw-items-center tw-pt-1 tw-text-xs tw-text-secondary-500"
          >
            <span v-if="isAdvancedFilter" class="tw-rotate-180 tw-pt-1"
              >&#9660;</span
            >
            <span v-else>&#9660;</span>
          </div>
        </div>
      </button>
      <cl-button
        variant="link-secondary"
        @on-click="clearLocalSearch"
        class="tw-absolute tw-bottom-1 tw-right-24 tw-mr-2"
        data-test-id="clear-search-button"
        >{{ $t("Clear Search") }}</cl-button
      >
    </div>
  </section>
  <!-- Pills -->
  <section>
    <div class="w-2/3 tw-mb-0 tw-mt-2 tw-flex tw-flex-wrap tw-items-center">
      <cl-active-filter
        :label="$t('Date From')"
        :value="start_date"
        :with-close="false"
        class="tw-my-2 tw-mr-2"
      />
      <cl-active-filter
        :label="$t('Date To')"
        :value="end_date"
        :with-close="false"
        class="tw-my-2 tw-mr-2"
      />
      <div v-for="(pill, index) in pills" :key="index">
        <cl-active-filter
          v-if="pill.condition && pill.label !== 'Message Type'"
          :label="$t(pill.label)"
          :value="pill.value"
          class="tw-my-2 tw-mr-2"
          @remove="actionsHandler(pill)"
          :with-close="!(pill.value === recipient && isRecipientStatic)"
        />
        <div
          v-if="pill.condition && pill.label === 'Message Type'"
          class="tw-flex"
        >
          <cl-active-filter
            v-for="(type, index) in pill.value"
            :key="index"
            :value="type.value"
            :label="pill.label"
            class="tw-my-2 tw-mr-2"
            @remove="clearMessageTypes(type.value)"
          />
        </div>
      </div>
    </div>
  </section>
  <!--HISTORY DATA TABLE -->
  <section id="data-table" class="tw--mt-2">
    <cl-data-table
      :data-test-id="'archive-reports-data-table'"
      :fetching="fetching"
      :headers="tableHeaders"
      :items="items"
      :default-sort-by="sortByCookie"
      :default-sort-desc="sortDirCookie"
      :default-items-on-page="maxItemsOnPage"
      with-set-columns
      @change-page="changePage"
      @sort-items="onSortItemsClick"
      @change-selected-columns="setSelectedColumnsCookie"
    >
      <template v-slot:actions>
        <cl-button variant="secondary" @on-click="refresh" :disabled="fetching">
          {{ $t("Refresh") }}
        </cl-button>
      </template>
      <template v-slot:[`body.cell.actions`]="props">
        <cl-data-table-view-action
          data-test-id="archive-reports-data-table-view-btn"
          @on-click="onEdit(props.trItem)"
        />
      </template>
      <template v-slot:[`body.cell.sender`]="props">
        <div class="tw-relative tw-inline-flex">
          <template v-if="props.tdValue.length > truncateMaxNumber">
            <cl-tooltip :text="props.tdValue" :id="props.tdValue">
              <span v-truncate> {{ props.tdValue }} </span>
            </cl-tooltip>
          </template>
          <template v-else>{{ props.tdValue }}</template>
        </div>
      </template>
      <template v-slot:[`body.cell.recipient`]="props">
        <div class="tw-relative tw-inline-flex">
          <template v-if="props.tdValue.length > truncateMaxNumber">
            <cl-tooltip :text="props.tdValue" :id="props.tdValue">
              <span v-truncate> {{ props.tdValue }} </span>
            </cl-tooltip>
          </template>
          <template v-else>{{ props.tdValue }}</template>
        </div>
      </template>
      <template v-slot:[`body.cell.smtp_reason`]="props">
        <div class="tw-relative tw-inline-flex">
          <template v-if="props.tdValue.length > truncateMaxNumber">
            <cl-tooltip :text="props.tdValue" :id="props.tdValue">
              <span v-truncate> {{ props.tdValue }} </span>
            </cl-tooltip>
          </template>
          <template v-else>{{ props.tdValue }}</template>
        </div>
      </template>
      <template v-slot:[`body.cell.subject`]="props">
        <div class="tw-relative tw-inline-flex">
          <template v-if="props.tdValue.length > truncateMaxNumber">
            <cl-tooltip :text="props.tdValue" :id="props.tdValue">
              <span v-truncate> {{ props.tdValue }} </span>
            </cl-tooltip>
          </template>
          <template v-else>{{ props.tdValue }}</template>
        </div>
      </template>
      <template v-slot:[`body.cell.client_address`]="props">
        <div class="tw-flex tw-items-center tw-gap-2">
          <div>
            <country-flag :country="props.trItem.country_of_origin_iso ?? ''" />
          </div>
          <div>{{ props.tdValue }}</div>
        </div>
      </template>
      <template v-slot:[`body.cell.message_type`]="props">
        <t-message-type :messageType="props.trItem.message_type">
          <template v-slot:messageType>
            <div class="tw-text-lg tw-leading-4">
              {{ props.trItem.message_type }}
            </div>
          </template>
          <template v-slot:details>
            <div class="tw-text-sm">
              {{ setMessageTypeDetails(props.trItem) }}
            </div>
          </template>
        </t-message-type>
      </template>
      <template v-slot:pagination>
        <div class="tw-grid tw-grid-cols-12 tw-gap-1">
          <div class="tw-col-span-12 md:tw-col-span-6">
            <div class="tw-relative">
              <div
                class="tw-mr-4 tw-inline-flex tw-h-10 tw-items-baseline tw-justify-start"
              >
                <label
                  class="tw-mr-4 tw-whitespace-nowrap tw-text-sm tw-text-dorian-gray-500"
                >
                  {{ $t("Rows per page:") }}
                </label>
                <select
                  v-model="maxItemsOnPage"
                  name="rows-per-page"
                  class="tw-focus:border-gray-200 peer tw-block tw-w-full tw-appearance-none tw-border-0 tw-border-b-2 tw-border-solid tw-border-dorian-gray-100/80 tw-bg-white tw-text-sm tw-text-dorian-gray-500 tw-outline-none focus:tw-border-secondary-500 focus:tw-ring-0"
                  @change="changeItemsOnPage"
                >
                  <option
                    v-for="value in itemsPerPage"
                    :key="value"
                    :value="value"
                    :selected="value === maxItemsOnPage"
                  >
                    {{ value }}
                  </option>
                </select>
              </div>
              <div
                v-if="items.length !== 0"
                class="tw-inline-flex tw-h-10 tw-items-baseline tw-justify-start"
              >
                <span class="tw-text-sm tw-text-dorian-gray-500"
                  >{{ indexPointer.start + 1 }}-{{
                    indexPointer.start + items.length
                  }}</span
                >
              </div>
            </div>
          </div>
          <div class="tw-col-span-12 md:tw-col-span-6">
            <div class="tw-relative tw-flex tw-justify-end">
              <button
                class="tw-bg-transparent tw-mr-[-1px] tw-rounded-l tw-border-2 tw-border-solid tw-border-secondary-500 tw-px-2 tw-text-sm tw-font-medium tw-text-dorian-gray-200 hover:tw-text-secondary-500 disabled:tw-cursor-default disabled:tw-border-r disabled:tw-border-dorian-gray-100/80 disabled:tw-text-dorian-gray-200"
                :disabled="currentPage === 1"
                @click="goPrev"
              >
                <span>&lt;</span>
              </button>
              <button
                class="tw-bg-transparent tw-ml-[-1px] tw-rounded-r tw-border-2 tw-border-solid tw-border-secondary-500 tw-px-2 tw-text-sm tw-font-medium tw-text-dorian-gray-200 hover:tw-text-secondary-500 disabled:tw-cursor-default disabled:tw-border-l disabled:tw-border-dorian-gray-100/80 disabled:tw-text-dorian-gray-200"
                :disabled="counter < dataOptions.results"
                @click="goNext"
              >
                <span>&gt;</span>
              </button>
            </div>
          </div>
        </div>
      </template>
    </cl-data-table>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CountryFlag from "vue-country-flag-next";
import dayjs from "dayjs";

import { useDataTable } from "@/composables/useDataTable";
import { useDataTableCookies } from "@/composables/useDataTableCookies";
import { usePills } from "@/composables/usePills";
import { checkDateRange } from "@/composables/useDateRange";
import getMessageType from "@/utils/get-message-type";
import {
  MESSAGE_TYPE,
  MESSAGE_DELIVERY_STATUS,
  MESSAGE_FLOW,
  COMPARE_OPTION,
  DATE_FORMAT,
  TIER,
  DEFAULT_OPTIONS,
  TRUNCATE,
  DATE_RANGE,
  ITEMS_PER_PAGE,
  RANGE,
  RECIPIENT,
  SENDER,
  SUBJECT,
  SCORE_COMPARE,
  SCORE,
  TYPE,
  ENVELOP_FROM,
  DELIVER_STATUS,
  ID,
  IP,
} from "@/constants";
import TMessageType from "@/components/TMessageType";
import { initialFiltersState } from "@/store/modules/emailsHistory.js";
import { ref, computed } from "vue";

const storeModule = "emailsHistory";

export default {
  name: "HistoryCommon",
  components: {
    CountryFlag,
    TMessageType,
  },
  props: {
    tier_id: {
      type: [String, Number],
      default: null,
    },
    tier: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const cookieKey = `data-table-history-${props.tier}`;
    const pillsKey = `h-${props.tier}-${props.tier_id}`;
    const STARTING_PAGE = 1;

    const {
      sortByCookie,
      sortDirCookie,
      itemsPerPageCookie,
      selectedColumnsCookie,
      setSortItemsCookie,
      setItemsPerPageCookie,
      setSelectedColumnsCookie,
    } = useDataTableCookies(
      {
        sortByKey: `${cookieKey}-sort-by`,
        sortDirKey: `${cookieKey}-sort-dir`,
        itemsPerPageKey: `${cookieKey}-items-per-page`,
        selectedColumnsKey: `${cookieKey}-selected-columns`,
      },
      {
        ...DEFAULT_OPTIONS,
        sortBy: "message_received",
      }
    );

    const {
      deliveryStatusLS,
      scoreLS,
      scoreCompareLS,
      typeLS,
      senderLS,
      recipientLS,
      envelopeFromLS,
      subjectLS,
      idLS,
      ipLS,
      setPills,
      clearPills,
      deletePill,
      dateRangeLS,
      startDateLS,
      endDateLS,
    } = usePills({
      deliveryStatusKey: `${pillsKey}-delivery_status`,
      scoreKey: `${pillsKey}-score`,
      scoreCompareKey: `${pillsKey}-score_compare`,
      typeKey: `${pillsKey}-type`,
      senderKey: `${pillsKey}-sender`,
      recipientKey: `${pillsKey}-recipient`,
      envelopeFromKey: `${pillsKey}-envelope_from`,
      subjectKey: `${pillsKey}-subject`,
      idKey: `${pillsKey}-id`,
      ipKey: `${pillsKey}-ip`,
      dateRangeKey: `${pillsKey}-date_range`,
      startDateKey: `${pillsKey}-start_date`,
      endDateKey: `${pillsKey}-end_date`,
    });

    const optionsBasedOnCookies = {
      sortBy: sortByCookie,
      sortDir: sortDirCookie,
      results: itemsPerPageCookie || DEFAULT_OPTIONS.results,
      page: 1,
    };

    const truncateMaxNumber = TRUNCATE.max;

    const {
      dataOptions,
      sortItems,
      changePage,
      changeItemsPerPage,
      debouncedSearch,
    } = useDataTable(optionsBasedOnCookies);

    const maxItemsOnPage = ref(dataOptions.value.results);
    const isAdvancedFilter = ref(false);

    const changeItemsOnPage = () => {
      currentPage.value = STARTING_PAGE;
      setItemsPerPageCookie(maxItemsOnPage.value);
      changeItemsPerPage(maxItemsOnPage.value, currentPage.value);
    };

    const indexPointer = computed(() => {
      const end = currentPage.value * maxItemsOnPage.value;
      const start = end - maxItemsOnPage.value;

      return { start, end };
    });

    const currentPage = ref(STARTING_PAGE);
    const goNext = () => {
      currentPage.value = currentPage.value + 1;
      changePage(currentPage.value);
    };

    const goPrev = () => {
      currentPage.value = currentPage.value - 1;
      changePage(currentPage.value);
    };

    return {
      debouncedSearch,
      dataOptions,
      sortItems,
      changePage,
      changeItemsPerPage,
      sortByCookie,
      sortDirCookie,
      itemsPerPageCookie,
      selectedColumnsCookie,
      setSortItemsCookie,
      setItemsPerPageCookie,
      setSelectedColumnsCookie,
      truncateMaxNumber,
      setPills,
      deletePill,
      deliveryStatusLS,
      scoreLS,
      scoreCompareLS,
      typeLS,
      senderLS,
      recipientLS,
      envelopeFromLS,
      subjectLS,
      idLS,
      ipLS,
      pillsKey,
      dateRangeLS,
      startDateLS,
      endDateLS,
      clearPills,
      goNext,
      goPrev,
      currentPage,
      changeItemsOnPage,
      maxItemsOnPage,
      indexPointer,
      isAdvancedFilter,
    };
  },
  data() {
    return {
      fetching: false,
      lastSearch: null,
      serverItemsLength: 0,
      dateFormat: { year: "numeric", month: "short", day: "2-digit" },
      messageTypeOptions: Object.entries(MESSAGE_TYPE).map(([value, text]) => ({
        value,
        text: this.$t(text),
      })),
      deliveryStatusOptions: Object.entries(MESSAGE_DELIVERY_STATUS).map(
        ([value, text]) => ({
          value,
          text: this.$t(text),
        })
      ),
      dateRangeOptions: Object.entries(DATE_RANGE).map(([value, text]) => ({
        value,
        text: this.$t(text),
      })),
      messageFlowOptions: Object.entries(MESSAGE_FLOW).map(([value, text]) => ({
        value,
        text: this.$t(text),
      })),
      scoreCompareOptions: Object.entries(COMPARE_OPTION).map(
        ([value, text]) => ({
          value,
          text: this.$t(text),
        })
      ),
      items: [],
      newCombinedArray: [],
      defaultFilters: initialFiltersState,
      itemsPerPage: ITEMS_PER_PAGE.history,
      isChange: false,
    };
  },
  computed: {
    ...mapGetters(storeModule, ["emails", "serverTotal", "counter", "filters"]),
    isDisabledDate() {
      return this.date_range !== "custom";
    },
    tableHeaders() {
      const headers = [
        { key: "message_received", label: this.$t("Date"), sortable: true },
        {
          key: "client_address",
          label: this.$t("Client Address"),
          sortable: false,
        },
        { key: "sender", label: this.$t("From"), sortable: true },
        { key: "recipient", label: this.$t("To"), sortable: true },
        {
          key: "message_type",
          label: this.$t("Message Type"),
          sortable: false,
        },
        { key: "subject", label: this.$t("Subject"), sortable: true },
        {
          key: "delivery_status",
          label: this.$t("Delivery Status"),
          sortable: false,
        },
        { key: "id", label: this.$t("SpamTitan ID"), sortable: true },
        { key: "actions", label: this.$t("Actions"), sticky: true },
      ];

      if (!this.selectedColumnsCookie?.length) {
        return headers;
      }

      return headers.map((header) => ({
        ...header,
        enabled: this.selectedColumnsCookie.includes(header.key),
      }));
    },
    date_range: {
      get() {
        return this.$store.state.emailsHistory.filters.date_range;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setDateRange`, value);
      },
    },
    start_date: {
      get() {
        return this.$store.state.emailsHistory.filters.start_date;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setStartDate`, value);
      },
    },
    end_date: {
      get() {
        return this.$store.state.emailsHistory.filters.end_date;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setEndDate`, value);
      },
    },
    sender: {
      get() {
        return this.$store.state.emailsHistory.filters.sender;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setSender`, value);
      },
    },
    recipient: {
      get() {
        return this.$store.state.emailsHistory.filters.recipient;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setRecipient`, value);
      },
    },
    envelope_from: {
      get() {
        return this.$store.state.emailsHistory.filters.envelope_from;
      },
      set(value) {
        this.$store.commit("emailsHistory/setEnvelopeFrom", value);
      },
    },
    subject: {
      get() {
        return this.$store.state.emailsHistory.filters.subject;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setSubject`, value);
      },
    },
    delivery_status: {
      get() {
        return this.$store.state.emailsHistory.filters.delivery_status;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setDeliveryStatus`, value);
      },
    },
    id: {
      get() {
        return this.$store.state.emailsHistory.filters.id;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setId`, value);
      },
    },
    ip: {
      get() {
        return this.$store.state.emailsHistory.filters.ip;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setIp`, value);
      },
    },
    message_flow: {
      get() {
        return this.$store.state.emailsHistory.filters.message_flow;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setMessageFlow`, value);
      },
    },
    type: {
      get() {
        return this.$store.state.emailsHistory.filters.type;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setMessageType`, value);
      },
    },
    score_compare: {
      get() {
        return this.$store.state.emailsHistory.filters.score_compare;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setScoreCompare`, value);
      },
    },
    score: {
      get() {
        return this.$store.state.emailsHistory.filters.score;
      },
      set(value) {
        this.$store.commit(`${storeModule}/setScore`, value);
      },
    },
    maxDate() {
      const today = new Date().toISOString().split("T")[0];
      return today;
    },
    pills() {
      return [
        {
          ...RANGE,
          condition: !!this.date_range && this.date_range !== "null",
          value: DATE_RANGE[this.date_range],
        },
        {
          ...RECIPIENT,
          condition: !!this.recipient,
          value: this.recipient,
        },
        {
          ...SENDER,
          condition: !!this.sender,
          value: this.sender,
        },
        {
          ...ENVELOP_FROM,
          condition: !!this.envelope_from,
          value: this.envelope_from,
        },
        {
          ...SUBJECT,
          condition: !!this.subject,
          value: this.subject,
        },
        {
          ...DELIVER_STATUS,
          condition: !!this.delivery_status && this.delivery_status !== "null",
          value: this.delivery_status,
        },
        {
          ...ID,
          condition: !!this.id,
          value: this.id,
        },
        {
          ...IP,
          condition: !!this.ip,
          value: this.ip,
        },
        {
          ...SCORE_COMPARE,
          condition:
            !!this.score &&
            this.score_compare !== "*" &&
            this.score_compare !== "between" &&
            !!this.score_compare,
          value: `${this.score_compare} ${this.score[0]}`,
        },
        {
          ...SCORE,
          condition:
            !!this.score &&
            this.score_compare !== "*" &&
            this.score_compare === "between",
          value: `${this.score_compare} ${this.score[0]} and ${this.score[1]}`,
        },
        {
          ...TYPE,
          condition: !!this.type,
          value: this.type,
        },
      ];
    },
    isRecipientStatic() {
      return this.tier === TIER.users;
    },
  },
  watch: {
    dataOptions: {
      handler() {
        this.submit();
      },
      deep: true,
      immediate: false,
    },
    emails: {
      handler() {
        const emails = this.emails.map((email) => ({
          ...email,
          virus: email.virus !== null ? email.virus.virus_name : "",
          sender: email.sender === "" ? this.$t("MAILER-DAEMON") : email.sender,
          message_received: `${dayjs(email.message_received).format(
            DATE_FORMAT.SHORT
          )}`,
          last_status_update: `${dayjs(email.last_status_update).format(
            DATE_FORMAT.FULL
          )}`,
        }));
        this.items = emails;
      },
      deep: true,
      immediate: true,
    },
    date_range: {
      handler() {
        checkDateRange(this.date_range, storeModule);
        if (!this.isDisabledDate) {
          this.isAdvancedFilter = true;
        }
      },
      deep: true,
      immediate: true,
    },
    isChange: {
      handler() {
        if (this.dataOptions.page > 1) {
          this.changePage(1);
          this.currentPage = 1;
        }
        this.onChangeAdvancedSearch();
        this.isChange = false;
      },
      deep: true,
      immediate: false,
    },
    type: {
      handler() {
        if (this.type.length > 0) {
          this.isChange = true;
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapActions(storeModule, [
      "fetchEmailsHistory",
      "setSelected",
      "clearEmailsList",
      "clearSearch",
    ]),
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("message", ["setHost"]),
    toggleAdvancedFilter() {
      this.isAdvancedFilter = !this.isAdvancedFilter;
    },
    replaceContentType(key) {
      return this.$t(getMessageType(key));
    },
    setMessageTypeDetails(item) {
      if (item.message_type === MESSAGE_TYPE.virus) {
        return item.virus;
      }
      if (item.message_type === MESSAGE_TYPE.spam) {
        return `${this.$t("Score")}: ${item.spam_level}`;
      }
      return;
    },
    async onEdit(message) {
      this.setSelected(message);
      const messageDetails = this.emails.find((m) => m.id === message.id);
      this.$store.commit("message/setMessageDetails", messageDetails);
      await this.setHost(message.host);
      let routerName = "";
      switch (this.tier) {
        case TIER.domains:
          routerName = "domain-history-message-overview";
          break;
        case TIER.users:
          routerName = "user-history-message-overview";
          break;
        case TIER.msps:
          routerName = "msp-history-message-overview";
          break;
        default:
          routerName = "customer-history-message-overview";
      }
      await this.$router.push({
        name: routerName,
        params: { id: message.id },
      });
    },
    onSortItemsClick(by, dir) {
      this.setSortItemsCookie(by, dir);
      this.sortItems(by, dir);
    },
    checkScoreValue() {
      const isScoreEmpty = this.score[0] === "";
      const isBetweenComparisonEmpty =
        this.score_compare === "between" &&
        (this.score[0] === "" || this.score[1] === "");

      if (this.score_compare && (isScoreEmpty || isBetweenComparisonEmpty)) {
        this.$store.commit(
          `${storeModule}/setScore`,
          this.defaultFilters.score
        );
        this.$store.commit(
          `${storeModule}/setScoreCompare`,
          this.defaultFilters.score_compare
        );
      }
    },
    clearLocalSearch() {
      this.clearSearch(this.isRecipientStatic);
      this.clearScore();
      this.clearPills(this.isRecipientStatic);
      this.submit();
    },
    refresh() {
      this.submit();
    },
    async submit() {
      this.checkScoreValue();
      try {
        this.fetching = true;
        this.clearEmailsList();

        await this.fetchEmailsHistory({
          tier: this.tier,
          id: this.tier_id,
          ...this.dataOptions,
        });
      } catch (_err) {
        // stub
      } finally {
        this.fetching = false;
        this.isChange = false;
      }
    },
    actionsHandler(pill) {
      const { action, cookieName } = pill;
      const { date_range, start_date, end_date } = this.defaultFilters;

      if (action === "setScore") {
        this.clearScore();
      } else if (action === "setDateRange") {
        this.$store.commit(`${storeModule}/${action}`, date_range);
        this.$store.commit(`${storeModule}/setStartDate`, start_date);
        this.$store.commit(`${storeModule}/setEndDate`, end_date);
        this.deletePill(`${this.pillsKey}-${cookieName}`);
        this.deletePill(`${this.pillsKey}-start_date`);
        this.deletePill(`${this.pillsKey}-end_date`);
      } else {
        this.$store.commit(`${storeModule}/${action}`, "");
        this.deletePill(`${this.pillsKey}-${cookieName}`);
      }
      this.submit();
    },
    clearMessageTypes(argName) {
      const filteredArray = this.type?.filter((item) => item.value !== argName);
      this.$store.commit(`${storeModule}/setMessageType`, filteredArray);
      this.deletePill(`${this.pillsKey}-type`);
      this.submit();
    },
    clearScore() {
      const { score, score_compare } = this.defaultFilters;
      const scoreCookieKey = `${this.pillsKey}-score`;
      const scoreCompareCookieKey = `${this.pillsKey}-score_compare`;

      this.$store.commit(`${storeModule}/setScore`, score);
      this.$store.commit(`${storeModule}/setScoreCompare`, score_compare);
      this.deletePill(scoreCompareCookieKey);
      this.deletePill(scoreCookieKey);
    },
    onChangeAdvancedSearch() {
      this.setPills({
        delivery_status: this.delivery_status,
        type: this.type,
        score: this.score,
        score_compare: this.score_compare,
        sender: this.sender,
        recipient: this.recipient,
        envelope_from: this.envelope_from,
        subject: this.subject,
        id: this.id,
        ip: this.ip,
        date_range: this.date_range,
        start_date: this.start_date,
        end_date: this.end_date,
      });
    },
  },
  async mounted() {
    this.setPageTitle(this.$t("History"));
    this.setPageSubtitle([
      this.$t(
        "Search and view recent messages for your domains to troubleshoot in realtime"
      ),
    ]);
    const localStorageKeys = [
      {
        key: this.recipientLS,
        mutation: `${storeModule}/setRecipient`,
      },
      { key: this.subjectLS, mutation: `${storeModule}/setSubject` },
      { key: this.senderLS, mutation: `${storeModule}/setSender` },
      {
        key: this.envelopeFromLS,
        mutation: `${storeModule}/setEnvelopeFrom`,
      },
      {
        key: this.deliveryStatusLS,
        mutation: `${storeModule}/setDeliveryStatus`,
      },
      { key: this.idLS, mutation: `${storeModule}/setId` },
      { key: this.ipLS, mutation: `${storeModule}/setIp` },
      { key: this.scoreLS, mutation: `${storeModule}/setScore` },
      {
        key: this.scoreCompareLS,
        mutation: `${storeModule}/setScoreCompare`,
      },
      {
        key: this.dateRangeLS,
        mutation: `${storeModule}/setDateRange`,
      },
      {
        key: this.typeLS,
        mutation: `${storeModule}/setMessageType`,
      },
      {
        key: this.startDateLS || this.defaultFilters.start_date,
        mutation: `${storeModule}/setStartDate`,
      },
      {
        key: this.endDateLS || this.defaultFilters.end_date,
        mutation: `${storeModule}/setEndDate`,
      },
    ];
    localStorageKeys.forEach(({ key, mutation }) => {
      if (key) {
        this.$store.commit(mutation, key);
        if (key === this.dateRangeCookie) {
          checkDateRange(key, storeModule);
        }
      }
    });
    this.submit();
  },
  beforeUnmount() {
    this.clearEmailsList();
  },
};
</script>
